import { type DBSchema, type IDBPDatabase, openDB } from 'idb'
import type { CachedAsset, PlayLogQueueItem, ScheduleCache } from '@/types/player.types'

interface PlayerDBSchema extends DBSchema {
  assets: {
    key: string
    value: CachedAsset
    indexes: { 'by-cachedAt': number }
  }
  schedule: {
    key: 'current'
    value: ScheduleCache & { key: 'current' }
  }
  playLogs: {
    key: number
    value: PlayLogQueueItem
    // `by-nextAttemptAt` só contém itens reenviáveis: dead-letter não tem `nextAttemptAt`.
    indexes: { 'by-status': string; 'by-nextAttemptAt': number }
  }
}

export type PlayerDB = IDBPDatabase<PlayerDBSchema>

export const DB_NAME = 'castiq-player'
/**
 * v1: assets, schedule, playLogs(by-status)
 * v2: playLogs(by-nextAttemptAt) — outbox de play logs com backoff por item (F-10).
 *     Linhas v1 não-dead (`pending`/`failed`) recebem `nextAttemptAt = 0` (elegíveis já).
 */
const DB_VERSION = 2

let db: PlayerDB | null = null

export async function getDB(): Promise<PlayerDB> {
  if (db) return db

  db = await openDB<PlayerDBSchema>(DB_NAME, DB_VERSION, {
    async upgrade(database, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        // Assets cache
        const assetStore = database.createObjectStore('assets', { keyPath: 'assetId' })
        assetStore.createIndex('by-cachedAt', 'cachedAt')

        // Schedule cache (key fixo 'current')
        database.createObjectStore('schedule')

        // Play log queue
        const logStore = database.createObjectStore('playLogs', {
          keyPath: 'id',
          autoIncrement: true
        })
        logStore.createIndex('by-status', 'status')
      }

      if (oldVersion < 2) {
        const logStore = transaction.objectStore('playLogs')
        logStore.createIndex('by-nextAttemptAt', 'nextAttemptAt')

        // Migra fila v1: itens `failed` ficavam órfãos após reload (só `pending` era relido).
        let cursor = await logStore.openCursor()
        while (cursor) {
          const item = cursor.value
          if (item.status !== 'dead' && item.nextAttemptAt === undefined) {
            await cursor.update({ ...item, status: 'pending', nextAttemptAt: 0 })
          }
          cursor = await cursor.continue()
        }
      }
    }
  })

  return db
}
